import { Link } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronRight, Clock, Minus, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';
import { cn } from '~/utils/cn';
import { formatDateShort, formatDateTimeShort, formatTimeOfDay, isSameDay } from '~/utils/date';

import {
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  isHiddenStage,
} from '../constants/hidden-stages';
import { useJourneyDeliveryOutcome } from '../hooks/use-journey-delivery-outcome';
import {
  type ProductRowFind,
  useProductEnrichedRow,
  useProductSampleRow,
} from '../hooks/use-product-stage-content';
import { useRunProductScoreDetails } from '../hooks/use-run-content';
import { useRunLabels } from '../hooks/use-run-labels';
import { useRunStageAttempts } from '../hooks/use-run-stage-attempts';
import type { LineageItem, Stage } from '../types/lineage-images.api.types';
import type {
  ProductScoreDetails,
  RunEnrichedProduct,
  SamplePreviewProduct,
} from '../types/run-content.api.types';
import type { StageAttempt } from '../types/run-detail.api.types';
import type { RunProgress } from '../types/run.api.types';
import { humanizeCode } from '../utils/catalog-labels';
import { enrichedFieldKind, isChangedKind, type EnrichedFieldKind } from '../utils/enriched-fields';
import { buildStageTimings } from '../utils/journey-timeline';
import { formatScope, itemStatusLabel, lineageReasonText } from '../utils/lineage-images-format';
import {
  batchReferenceReason,
  isStoppedRunStatus,
  JOURNEY_CONTENT_STAGES,
  JOURNEY_LEDGER_STAGES,
  ordinal,
  stageBarState,
  type StageBarState,
  stageProgressOf,
  stageRetryGroups,
  worseBarState,
} from '../utils/product-history';
import { gateRedirectSearch } from '../utils/run-decisions';
import { DASH, formatLift, formatScore } from '../utils/run-format';
import { fieldScoresAscending, HEADLINE_SCORE } from '../utils/run-score-fields';
import { skipReasonText } from '../utils/skip-code';

import { RunNameLink } from './run-name';
import { StageRetryNote } from './stage-retry-note';

/** Glyph per ledger-item / coarse stage outcome — colour only for problems (G1). */
const STATUS_ICON: Record<string, { Icon: typeof Check; className: string }> = {
  SUCCESS: { Icon: Check, className: 'text-foreground' },
  COMPLETED: { Icon: Check, className: 'text-foreground' },
  FAILED: { Icon: X, className: 'text-error' },
  REVISIT: { Icon: TriangleAlert, className: 'text-warning' },
  RECONCILE_FAILED: { Icon: TriangleAlert, className: 'text-warning' },
  PENDING: { Icon: Clock, className: 'text-muted-foreground' },
  RUNNING: { Icon: Clock, className: 'text-muted-foreground' },
  AWAITING_GATE: { Icon: Clock, className: 'text-warning' },
  SKIPPED: { Icon: Minus, className: 'text-muted-foreground' },
};

/** One row's status icon + label, from a ledger item or a coarse StageProgress/StageAttempt status. */
function StatusGlyph({ status, label }: { status: string; label: string }) {
  const style = STATUS_ICON[status] ?? STATUS_ICON.PENDING;
  const Icon = style?.Icon ?? Clock;
  return (
    <span className={cn('inline-flex items-center gap-1', style?.className)} title={label}>
      <Icon aria-hidden="true" className="size-3.5" />
      <span className="text-xs">{label}</span>
    </span>
  );
}

/**
 * One attempt's outcome, oldest first: "1st ok" / "2nd failed (reason)" — or, for
 * SUPERSEDED_BY_LATER_RUN / RUN_PRODUCT_HELD, a clean phrase + batch link instead of ever
 * rendering `lineageReasonText`'s humanized-UUID text for those two codes (W3 owns the real fix
 * upstream in `utils/lineage-images-format.ts`; `batchReferenceReason` is the local stand-in).
 */
function AttemptPhrase({ item }: { item: LineageItem }) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { t: tLineage } = useTranslation('catalog-v2-lineage-images');
  if (item.status !== 'FAILED') {
    return <>{t('journey.attemptOk', { ordinal: ordinal(item.attempt) })}</>;
  }
  const ref = batchReferenceReason(item);
  if (ref) {
    return (
      <>
        {t('journey.attemptFailedNoReason', { ordinal: ordinal(item.attempt) })} (
        {t(ref.kind === 'held' ? 'journey.heldByBatch' : 'journey.supersededByBatch')}{' '}
        <RunNameLink runId={ref.runId} className="text-xs" />)
      </>
    );
  }
  const reason = lineageReasonText(tLineage, item);
  return (
    <>
      {reason
        ? t('journey.attemptFailed', { ordinal: ordinal(item.attempt), reason })
        : t('journey.attemptFailedNoReason', { ordinal: ordinal(item.attempt) })}
    </>
  );
}

/**
 * A ledger stage (Feed intake / Matching): exact per-product attempts from the lineage
 * read, GROUPED BY MARKET (ledger `scope`) — a run can carry more than one market listing for the
 * same product (e.g. UK + US), and each market has its own attempt sequence and final outcome.
 * The header line shows "UK · en ✓ · US · en ✓"; the muted ↻ attempts note (`StageRetryNote`,
 * shared with the journey drawer and product history card) appears only under a market that was a
 * REAL retry — more than one attempt and at least one failure (design round 2, item 4B) — never
 * the old "N attempts: 2nd ok, 2nd ok" line that mixed two markets' single attempts together.
 */
function LedgerStageSection({
  stage,
  items,
  foldedItems = [],
  reached,
  expanded,
  onToggle,
  destination,
}: {
  stage: Stage;
  items: LineageItem[];
  /** Feed intake only: this product's hidden Image check attempts, listed "Product image · …"
   *  in the expanded list so a product stopped there still says why (hidden-stages.ts). */
  foldedItems?: LineageItem[];
  /** Finding #7 — true when the stage bar says this batch reached this stage (run-level status)
   *  even though `items` came back empty: an explicit "couldn't load" beats a flat "not reached". */
  reached: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** This batch's resolved connector name, so DELIVERY would read "Publish to X" here too. */
  destination: string | null;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { t: tLineage } = useTranslation('catalog-v2-lineage-images');
  const { stageLabel } = useRunLabels();
  const label = stageLabel(stage, destination);

  const byScope = new Map<string, LineageItem[]>();
  for (const item of items) {
    byScope.set(item.scope, [...(byScope.get(item.scope) ?? []), item]);
  }
  const marketGroups = [...byScope.entries()].map(([scope, groupItems]) => {
    const sorted = [...groupItems].sort((a, b) => a.attempt - b.attempt);
    return { scope, sorted, newest: sorted[sorted.length - 1] };
  });
  const retryGroups = stageRetryGroups({ ledgerItems: items }, stage);

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="ghost"
        onClick={onToggle}
        aria-expanded={expanded}
        className="h-auto w-full cursor-pointer justify-start gap-2 p-0 text-left font-normal whitespace-normal hover:bg-transparent"
      >
        {expanded ? (
          <ChevronDown aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        ) : (
          <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        )}
        <span className="text-foreground min-w-28 shrink-0 font-medium">{label}</span>
        {marketGroups.length === 0 ? (
          <span className="text-muted-foreground text-xs">
            {t(reached ? 'journey.ledgerRowsUnavailable' : 'journey.notReached')}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {marketGroups.map(({ scope, newest }) => {
              if (!newest) return null;
              const market = formatScope(scope);
              return (
                <span key={scope} className="inline-flex items-center gap-1">
                  {market ? <span className="text-muted-foreground text-xs">{market}</span> : null}
                  <StatusGlyph
                    status={newest.status}
                    label={itemStatusLabel(tLineage, newest.status)}
                  />
                </span>
              );
            })}
          </span>
        )}
      </Button>
      {retryGroups.map((group) => (
        <StageRetryNote key={`${group.stage}|${group.scope}`} group={group} stageLabel={label} />
      ))}
      {expanded ? (
        <ol className="text-muted-foreground flex flex-col gap-1 pl-6 text-xs">
          {marketGroups.flatMap(({ scope, sorted }) => {
            const market = formatScope(scope);
            return sorted.map((item) => (
              <li
                key={`${scope}|${String(item.attempt)}|${item.createdAt}`}
                className="tabular-nums"
              >
                {market ? `${market} · ` : ''}
                {t('journey.attemptsFold', { count: 1 }).replace(':', '')} {item.attempt} ·{' '}
                {formatDateTimeShort(item.createdAt)} · <AttemptPhrase item={item} />
              </li>
            ));
          })}
          {[...foldedItems]
            .sort((a, b) => a.attempt - b.attempt)
            .map((item) => {
              const market = formatScope(item.scope);
              return (
                <li
                  key={`folded|${item.scope}|${String(item.attempt)}|${item.createdAt}`}
                  className="tabular-nums"
                >
                  {t('journey.atImageCheck')} · {market ? `${market} · ` : ''}
                  {t('journey.attemptsFold', { count: 1 }).replace(':', '')} {item.attempt} ·{' '}
                  {formatDateTimeShort(item.createdAt)} · <AttemptPhrase item={item} />
                </li>
              );
            })}
        </ol>
      ) : null}
    </div>
  );
}

/** One content-generation / delivery stage: coarse status always shown, exact figures and the
 *  stage-level attempt history fetched only once this section is expanded (bounded, user-initiated).
 *  ENRICHMENT shows counts + the hand-off id only — the full field diffs live in the page's own
 *  "Content generation changes" section above (moved there per design round 2, item 7), not
 *  duplicated inside this accordion. */
function ContentStageSection({
  runId,
  productId,
  feedProductId,
  stage,
  progressStatus,
  refIds,
  expanded,
  onToggle,
  destination,
  isOpenGate,
}: {
  runId: string;
  productId: string;
  feedProductId: string | null;
  stage: Stage;
  progressStatus: string | undefined;
  refIds: Record<string, string[]> | undefined;
  expanded: boolean;
  onToggle: () => void;
  /** This batch's resolved connector name, so DELIVERY reads "Publish to X" here. */
  destination: string | null;
  /** True when this is the stage the batch's open gate is waiting at — shows a visible
   *  "Review ›" link straight to the decision screen (CLARITY_BATCH_BRIEF.md S2's row action). */
  isOpenGate: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { statusLabel, stageLabel } = useRunLabels();
  const label = stageLabel(stage, destination);

  // Score stages are a single cheap per-product read — safe to fetch whenever the section is open,
  // not only on expand, so the collapsed row can show the real number.
  const isScoreStage = stage === 'PRE_ENRICH_SCORE' || stage === 'POST_ENRICH_SCORE';
  const score = useRunProductScoreDetails(
    runId,
    isScoreStage ? productId : null,
    stage === 'PRE_ENRICH_SCORE' ? 'PRE' : 'POST',
  );
  const sample = useProductSampleRow(runId, productId, expanded && stage === 'SAMPLE');
  const enriched = useProductEnrichedRow(runId, productId, expanded && stage === 'ENRICHMENT');
  const delivery = useJourneyDeliveryOutcome(
    { runId, feedProductId },
    expanded && stage === 'DELIVERY',
  );
  const attempts = useRunStageAttempts(runId, stage, expanded);

  // `refIds` never carries a `generationRunId` key (the allowlist is syncId / batchId /
  // externalJobRef / deliveryStatus / …) — that fallback was dead; batchId / externalJobRef cover
  // every hand-off id this allowlist actually sends.
  const handoffId = refIds?.batchId?.[0] ?? refIds?.externalJobRef?.[0] ?? null;

  const overallScore = (values: typeof score.data) => {
    const overall = values?.scores?.find((s) => s.name === HEADLINE_SCORE)?.value;
    return overall ?? values?.product?.overallScore ?? null;
  };

  const summary = (() => {
    if (isScoreStage) {
      const value = overallScore(score.data);
      if (value !== null) return t('journey.scoreValue', { value: formatScore(value) });
      return score.isError ? t('journey.readError') : t('journey.scoreNotScored');
    }
    // Everything else: the run's own per-stage status is the honest collapsed summary; the exact
    // per-product figure loads once this row is expanded.
    return progressStatus ? statusLabel(progressStatus) : t('journey.notReached');
  })();

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onToggle}
          aria-expanded={expanded}
          className="h-auto min-w-0 flex-1 cursor-pointer justify-start gap-2 p-0 text-left font-normal whitespace-normal hover:bg-transparent"
        >
          {expanded ? (
            <ChevronDown aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
          ) : (
            <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
          )}
          <span className="text-foreground min-w-28 shrink-0 font-medium">{label}</span>
          <StatusGlyph status={progressStatus ?? 'PENDING'} label={summary} />
        </Button>
        {isOpenGate ? (
          <Link
            to="/catalog-v2/runs/$runId/products"
            params={{ runId }}
            search={gateRedirectSearch(stage, productId, null)}
            className="text-primary shrink-0 cursor-pointer text-xs whitespace-nowrap hover:underline"
          >
            {t('journey.reviewNow')} ›
          </Link>
        ) : null}
      </div>

      {expanded ? (
        <div className="text-foreground flex flex-col gap-2 pl-6 text-sm">
          {handoffId ? (
            <p className="text-muted-foreground flex items-center gap-1 text-xs">
              {t('journey.handoffId', { id: handoffId })}
              <CopyIconButton text={handoffId} label={t('journey.copyId')} />
            </p>
          ) : null}

          {stage === 'PRE_ENRICH_SCORE' || stage === 'POST_ENRICH_SCORE' ? (
            <ScoreBody data={score.data} isError={score.isError} />
          ) : null}

          {stage === 'SAMPLE' ? <SampleBody find={sample.data} isError={sample.isError} /> : null}

          {stage === 'ENRICHMENT' ? (
            <>
              <EnrichmentCountsBody find={enriched.data} isError={enriched.isError} />
              <JourneyCompareLink runId={runId} productId={productId} />
            </>
          ) : null}

          {stage === 'APPROVAL' ? (
            <ApprovalBody attempts={attempts.data?.attempts} isError={attempts.isError} />
          ) : null}

          {stage === 'DELIVERY' ? (
            <>
              <DeliveryBody status={delivery.status} rows={delivery.rows} />
              <JourneyCompareLink runId={runId} productId={productId} />
            </>
          ) : null}

          {!isScoreStage && stage !== 'APPROVAL' ? (
            <StageAttemptsList attempts={attempts.data?.attempts} isError={attempts.isError} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** PRE / POST score expanded body: weakest field and any failure the score read carries. */
function ScoreBody({
  data,
  isError,
}: {
  data: ProductScoreDetails | null | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { fieldLabel } = useRunLabels();
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!data) return <p className="text-muted-foreground text-xs">{t('journey.scoreNotScored')}</p>;
  const record = Object.fromEntries((data.scores ?? []).map((s) => [s.name, s.value]));
  const weakest = fieldScoresAscending(record)[0];
  return (
    <div className="flex flex-col gap-1 text-xs">
      {weakest ? <p>{t('journey.scoreWeakest', { field: fieldLabel(weakest.name) })}</p> : null}
      {data.product?.errorMessage ? (
        <p className="text-error">{data.product.errorMessage}</p>
      ) : null}
    </div>
  );
}

/** Sample stage expanded body: in/out of sample, the lift and any per-product score error. */
function SampleBody({
  find,
  isError,
}: {
  find: ProductRowFind<SamplePreviewProduct> | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!find) return null; // still walking the sample pages
  const row = find.row;
  if (!row) {
    return (
      <p className="text-muted-foreground text-xs">
        {find.truncated
          ? t('journey.sampleTruncated', { scanned: find.scanned })
          : t('journey.sampleNotIn')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <p>
        {t('journey.sampleIn')}
        {row.lift !== null ? ` · ${t('journey.sampleLift', { lift: formatLift(row.lift) })}` : ''}
      </p>
      {row.errorMessage ? <p className="text-error">{row.errorMessage}</p> : null}
    </div>
  );
}

/** Enrichment stage's Journey-row body: counts only (data in → out), no field diffs — those are
 *  the page's own "Content generation changes" section (`EnrichmentChangesSection` below), so a
 *  product's changed fields are shown exactly once per page, not duplicated in the accordion. */
export function EnrichmentCountsBody({
  find,
  isError,
}: {
  find: ProductRowFind<RunEnrichedProduct> | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { t: tCg } = useTranslation('catalog-v2-content-gen');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!find) return null;
  const row = find.row;
  if (!row) {
    return (
      <p className="text-muted-foreground text-xs">
        {find.truncated
          ? t('journey.enrichmentTruncated', { scanned: find.scanned })
          : t('journey.sampleNotIn')}
      </p>
    );
  }
  // R12 — a product the run's scope left out says why, as a label (never the raw skip text).
  const skipped = skipReasonText(tCg, row.skipCode, row.errorMessage);
  if (skipped) return <p className="text-muted-foreground text-xs">{skipped}</p>;
  const counts = row.fields.reduce(
    (acc, field) => {
      const kind: EnrichedFieldKind = enrichedFieldKind(field);
      if (kind === 'failed') acc.failed += 1;
      else if (isChangedKind(kind)) acc.changed += 1;
      else acc.unchanged += 1;
      return acc;
    },
    { changed: 0, unchanged: 0, failed: 0 },
  );
  return (
    <p className="text-muted-foreground text-xs">
      {t('journey.enrichmentSummary', {
        changed: counts.changed,
        unchanged: counts.unchanged,
        failed: counts.failed,
      })}
    </p>
  );
}

/** Approval stage expanded body: the gate decision that closed it, from the stage's own attempts
 *  (no per-product approval read exists — a run's approval gate decides the whole run at once). */
function ApprovalBody({
  attempts,
  isError,
}: {
  attempts: StageAttempt[] | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  // Attempts arrive oldest-first — the LATEST decided attempt wins after a retry re-decides.
  const decided = [...(attempts ?? [])].reverse().find((a) => a.gateOutcome);
  if (!decided) {
    return <p className="text-muted-foreground text-xs">{t('journey.approvalNotDecided')}</p>;
  }
  return (
    <p className="text-xs">
      {decided.gateOutcome === 'APPROVE'
        ? t('journey.approvalApproved')
        : t('journey.approvalRejected')}
    </p>
  );
}

/** Delivery stage expanded body: every destination this product's source id fanned out to (never
 *  just the first row — one source id can reach several destinations). */
function DeliveryBody({
  status,
  rows,
}: {
  status: 'idle' | 'loading' | 'no-job' | 'error' | 'no-connection' | 'not-found' | 'found';
  rows: ProductLookupRow[];
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  switch (status) {
    case 'no-job':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNoJob')}</p>;
    case 'error':
      return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
    // Resolving the connection id is still a read in flight — never say "not available" for it.
    case 'idle':
    case 'loading':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryResolving')}</p>;
    case 'no-connection':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNoConnection')}</p>;
    case 'not-found':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNotSent')}</p>;
    case 'found':
      return (
        <ul className="flex flex-col gap-1.5 text-xs">
          {rows.map((row, i) => (
            <li key={row.id ?? i} className="flex flex-col gap-0.5">
              {row.destinationType ? (
                <span className="text-foreground font-medium">
                  {humanizeCode(row.destinationType)}
                </span>
              ) : null}
              <span>{humanizeCode(row.outcome ?? row.syncStatus ?? '')}</span>
              {(() => {
                // The wire sends "" rather than omitting a field — treat it as absent too.
                const detail = [row.errorMessage, row.syncStatusReason, row.reasonLabel].find(
                  (v) => !!v,
                );
                return detail ? <span className="text-error">{detail}</span> : null;
              })()}
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

/** This stage's own retry history (run-wide — the stage was retried as a whole, this product
 *  included), oldest first; hidden with ≤1 attempt since the row above already says the outcome. */
function StageAttemptsList({
  attempts,
  isError,
}: {
  attempts: StageAttempt[] | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { statusLabel } = useRunLabels();
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!attempts || attempts.length <= 1) return null;
  return (
    <ol className="text-muted-foreground flex flex-col gap-0.5 text-xs">
      {attempts.map((a) => (
        <li key={a.attempt} className="tabular-nums">
          {a.attempt} · {statusLabel(a.status)}
          {a.startedAt ? ` · ${formatDateTimeShort(a.startedAt)}` : ''}
          {a.failureReason ? ` — ${a.failureReason}` : ''}
        </li>
      ))}
    </ol>
  );
}

/** Rail dot fill per bar state — only `current` (amber) / `failed` (red) carry a problem/attention
 *  colour (G1), matching `StageSegmentBar`'s palette so the two timelines read as one language. */
const DOT_CLASS: Record<StageBarState, string> = {
  pending: 'bg-background border-2 border-muted-foreground/40',
  done: 'bg-foreground/70',
  skipped: 'bg-foreground/70',
  current: 'bg-warning',
  failed: 'bg-error',
};

/** The rail segment connecting one dot to the next: solid once the stage behind it finished
 *  (red when it failed), dotted while still current/ahead — "solid rail done, dotted waiting/ahead"
 *  (design round 3, S2). */
function edgeLineClass(state: StageBarState): string {
  if (state === 'failed') return 'border-l-2 border-solid border-error/50';
  if (state === 'done' || state === 'skipped')
    return 'border-l-2 border-solid border-foreground/30';
  return 'border-l-2 border-dashed border-muted-foreground/30';
}

/**
 * One stage's slot on the Journey rail: a status dot connected to its neighbours, the product's
 * own clock time on the left (date shown only when it changes from the row above), the stage's
 * duration on the right, and the stage's own header+body as children. A small amber ring marks a
 * stage that was genuinely retried — "an extra small dot on the rail" (design round 3, S2).
 */
function TimelineRow({
  state,
  hasRetry,
  topLine,
  bottomLine,
  dateLabel,
  timeLabel,
  durationText,
  isOngoing,
  children,
}: {
  state: StageBarState;
  hasRetry: boolean;
  /** Line class for the segment above this dot, or `null` on the first row (nothing to draw). */
  topLine: string | null;
  /** Line class for the segment below this dot, or `null` on the last row. */
  bottomLine: string | null;
  dateLabel: string | null;
  timeLabel: string | null;
  durationText: string | null;
  isOngoing: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  return (
    <li className="flex gap-3">
      <div className="flex w-16 shrink-0 flex-col items-end pt-0.5">
        {dateLabel ? (
          <span className="text-muted-foreground text-xs leading-tight">{dateLabel}</span>
        ) : null}
        <span className="text-muted-foreground text-xs leading-tight tabular-nums">
          {timeLabel ?? DASH}
        </span>
      </div>
      <div aria-hidden="true" className="flex flex-col items-center">
        <span className={cn('w-0 flex-1', topLine ?? 'invisible border-l-2')} />
        <span
          className={cn(
            'ring-background size-3 shrink-0 rounded-full ring-4',
            DOT_CLASS[state],
            hasRetry && 'ring-warning ring-2',
          )}
        />
        <span className={cn('w-0 flex-1', bottomLine ?? 'invisible border-l-2')} />
      </div>
      <div className="min-w-0 flex-1 pb-4">{children}</div>
      <div className="flex w-20 shrink-0 justify-end pt-0.5 text-right">
        <span
          className={cn(
            'text-xs tabular-nums',
            isOngoing ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {durationText
            ? isOngoing
              ? t('journey.waitingDuration', { time: durationText })
              : durationText
            : ''}
        </span>
      </div>
    </li>
  );
}

/** The trailing not-reached stages, collapsed into one muted rail line instead of a dot each
 *  (design round 3, S2 — "Not-reached stages collapse into one muted line"). */
function TimelineNotReachedRow({
  topLine,
  labels,
  stoppedAtLabel,
}: {
  topLine: string | null;
  labels: string[];
  stoppedAtLabel: string | null;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  return (
    <li className="flex gap-3">
      <div className="w-16 shrink-0" />
      <div aria-hidden="true" className="flex flex-col items-center">
        <span className={cn('w-0 flex-1', topLine ?? 'invisible border-l-2')} />
        <span
          className={cn('ring-background size-3 shrink-0 rounded-full ring-4', DOT_CLASS.pending)}
        />
      </div>
      <p className="text-muted-foreground min-w-0 flex-1 pb-1 text-xs">
        {stoppedAtLabel
          ? t('journey.notReachedStopped', { stages: labels.join(' · '), stage: stoppedAtLabel })
          : t('journey.notReachedYet', { stages: labels.join(' · ') })}
      </p>
      <div className="w-20 shrink-0" />
    </li>
  );
}

export interface ProductJourneyDetailProps {
  runId: string;
  productId: string;
  feedProductId: string | null;
  /** This product's ledger items for THIS run only (IMPORT / IMAGE_GATE / CURATION, every attempt). */
  runItems: LineageItem[];
  progress: RunProgress | undefined;
  /** This batch's resolved connector name, so DELIVERY reads "Publish to X" here. */
  destination: string | null;
  /** Opens this stage's row expanded on first render (e.g. a step the caller already knows about). */
  initialStage?: Stage | undefined;
  readError?: boolean;
}

/** Every stage the Journey rail can show, in flow order (ledger stages first, matching
 *  `flowStagesFor`'s default — the 9-stage list every flow in practice follows), minus the hidden
 *  Image check stage, which folds into Feed intake's row instead (hidden-stages.ts). */
const ALL_JOURNEY_STAGES: readonly Stage[] = [
  ...JOURNEY_LEDGER_STAGES,
  ...JOURNEY_CONTENT_STAGES,
].filter((stage) => !isHiddenStage(stage));
const LEDGER_STAGE_SET = new Set<Stage>(JOURNEY_LEDGER_STAGES);

/** This product's newest ledger state at one stage, else the run-level stage status. */
function ledgerRowState(
  stage: Stage,
  runItems: LineageItem[],
  progress: RunProgress | undefined,
): StageBarState {
  const newest = [...runItems]
    .filter((i) => i.stage === stage)
    .sort((a, b) => a.attempt - b.attempt)
    .at(-1);
  return stageBarState(newest?.status ?? stageProgressOf(progress, stage)?.status);
}

/**
 * The Journey section of the product detail page (design round 3, S2 — "activity timeline"):
 * every stage of this batch on a vertical rail, with its start time and duration, every attempt,
 * data in → out, delivery rows — the full detail the journey drawer used to show inline before it
 * became a one-glance summary. Any number of rows can be open at once (Expand all / Collapse all);
 * ENRICHMENT shows counts only here (its field diffs live in the page's own Content generation
 * changes section). Trailing stages this batch hasn't reached yet collapse into one muted line.
 */
export function ProductJourneyDetail({
  runId,
  productId,
  feedProductId,
  runItems,
  progress,
  destination,
  initialStage,
  readError = false,
}: ProductJourneyDetailProps) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { stageLabel } = useRunLabels();
  const [expanded, setExpanded] = useState<Set<Stage>>(
    () => new Set<Stage>(initialStage ? [initialStage] : []),
  );

  if (readError) {
    return <p className="text-muted-foreground py-4 text-sm">{t('journey.readError')}</p>;
  }

  const timings = buildStageTimings(ALL_JOURNEY_STAGES, runItems, progress);
  const rows = ALL_JOURNEY_STAGES.map((stage) => {
    if (LEDGER_STAGE_SET.has(stage)) {
      // Same fallback chain the journey drawer's stage bar uses: this product's own newest
      // ledger item, else the run-level stage status — so the dot agrees with the bar even
      // when `items` is empty because this product's own rows failed to load.
      const own = ledgerRowState(stage, runItems, progress);
      if (stage !== IMAGE_CHECK_FOLD_TARGET || own !== 'done') return { stage, state: own };
      // Feed intake finished: the hidden Image check's outcome is now part of this row.
      const folded = ledgerRowState(IMAGE_CHECK_STAGE, runItems, progress);
      return { stage, state: worseBarState(own, folded) };
    }
    return { stage, state: stageBarState(stageProgressOf(progress, stage)?.status) };
  });

  // Trailing run of not-yet-reached stages collapses into one muted line (design round 3, S2).
  let splitIndex = rows.length;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.state !== 'pending') break;
    splitIndex = i;
  }
  const visibleRows = rows.slice(0, splitIndex);
  const notReachedRows = rows.slice(splitIndex);

  // Each row's date/retry/line metadata, computed once up front (plain loop, not inside the JSX
  // map below) so "date shown only when it changes" can track the previous row without mutating a
  // variable from inside a render-time callback.
  const rowMeta: {
    stage: Stage;
    state: StageBarState;
    startedAt: string | null;
    durationText: string | null;
    isOngoing: boolean;
    showDate: boolean;
    hasRetry: boolean;
  }[] = [];
  let lastShownDate: string | null = null;
  for (const { stage, state } of visibleRows) {
    const timing = timings.get(stage);
    const startedAt = timing?.startedAt ?? null;
    // The date only repeats on screen when it actually changes from the row above — a batch
    // that runs well past midnight still reads as one continuous timeline otherwise.
    const showDate = !!startedAt && (!lastShownDate || !isSameDay(startedAt, lastShownDate));
    if (startedAt) lastShownDate = startedAt;
    rowMeta.push({
      stage,
      state,
      startedAt,
      durationText: timing?.durationText ?? null,
      isOngoing: !!timing?.isOngoing,
      showDate,
      hasRetry:
        LEDGER_STAGE_SET.has(stage) &&
        stageRetryGroups({ ledgerItems: runItems }, stage).length > 0,
    });
  }

  const toggle = (stage: Stage) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };
  const expandAll = () => {
    setExpanded(new Set(visibleRows.map((r) => r.stage)));
  };
  const collapseAll = () => {
    setExpanded(new Set());
  };

  const stoppedAtLabel = isStoppedRunStatus(progress?.runStatus)
    ? stageLabel(progress?.currentStage, destination)
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={expandAll}
          disabled={visibleRows.length === 0}
          className="cursor-pointer text-xs disabled:cursor-not-allowed"
        >
          {t('journey.expandAll')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={collapseAll}
          disabled={expanded.size === 0}
          className="cursor-pointer text-xs disabled:cursor-not-allowed"
        >
          {t('journey.collapseAll')}
        </Button>
      </div>
      <ol className="flex flex-col">
        {rowMeta.map(
          ({ stage, state, startedAt, durationText, isOngoing, showDate, hasRetry }, index) => {
            return (
              <TimelineRow
                key={stage}
                state={state}
                hasRetry={hasRetry}
                topLine={index > 0 ? edgeLineClass(rowMeta[index - 1]?.state ?? 'pending') : null}
                bottomLine={
                  index < rowMeta.length - 1 || notReachedRows.length > 0
                    ? edgeLineClass(state)
                    : null
                }
                dateLabel={showDate && startedAt ? formatDateShort(startedAt) : null}
                timeLabel={startedAt ? formatTimeOfDay(startedAt) : null}
                durationText={durationText}
                isOngoing={isOngoing}
              >
                {LEDGER_STAGE_SET.has(stage) ? (
                  <LedgerStageSection
                    stage={stage}
                    items={runItems.filter((i) => i.stage === stage)}
                    foldedItems={
                      stage === IMAGE_CHECK_FOLD_TARGET
                        ? runItems.filter((i) => i.stage === IMAGE_CHECK_STAGE)
                        : []
                    }
                    reached={state !== 'pending'}
                    expanded={expanded.has(stage)}
                    onToggle={() => {
                      toggle(stage);
                    }}
                    destination={destination}
                  />
                ) : (
                  <ContentStageSection
                    runId={runId}
                    productId={productId}
                    feedProductId={feedProductId}
                    stage={stage}
                    progressStatus={stageProgressOf(progress, stage)?.status}
                    refIds={stageProgressOf(progress, stage)?.refIds}
                    expanded={expanded.has(stage)}
                    onToggle={() => {
                      toggle(stage);
                    }}
                    destination={destination}
                    isOpenGate={progress?.pendingGate?.stage === stage}
                  />
                )}
              </TimelineRow>
            );
          },
        )}
        {notReachedRows.length > 0 ? (
          <TimelineNotReachedRow
            topLine={rowMeta.length > 0 ? edgeLineClass(rowMeta.at(-1)?.state ?? 'pending') : null}
            labels={notReachedRows.map((r) => stageLabel(r.stage, destination))}
            stoppedAtLabel={stoppedAtLabel}
          />
        ) : null}
      </ol>
    </div>
  );
}

/** DOM id of the product page's "Content generation changes" section — the field-by-field
 *  source → generated → sent table (it replaced the separate Compare page). */
export const CONTENT_CHANGES_ANCHOR = 'content-changes';

/** "Field-by-field changes ›" from the journey drawer — opens this product's page at its
 *  source → generated → sent table, the one place that comparison now lives. */
export function JourneyCompareLink({ runId, productId }: { runId: string; productId: string }) {
  const { t } = useTranslation('catalog-v2-product-master');
  return (
    <Link
      to="/catalog-v2/runs/$runId/products/$productId"
      params={{ runId, productId }}
      hash={CONTENT_CHANGES_ANCHOR}
      className="text-primary w-fit cursor-pointer text-xs hover:underline"
    >
      {t('journey.compareSourceGenerated')}
    </Link>
  );
}
